import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { BidMatrixDialog } from "@/components/BidMatrixDialog";
import { FileArrowDown, MagnifyingGlass, Package, Table as TableIcon } from "@phosphor-icons/react";

const fmt = (n) => !n ? "—" : n.toLocaleString();

const STATUS_STYLE = {
  Pending: "bg-amber-50 text-amber-800 border-amber-200",
  Committed: "bg-blue-50 text-blue-800 border-blue-200",
  Certified: "bg-slate-100 text-slate-700 border-slate-200",
  Funded: "bg-emerald-50 text-emerald-800 border-emerald-200",
  Cancelled: "bg-red-50 text-red-800 border-red-200",
  Rejected: "bg-red-50 text-red-800 border-red-200",
};

const StatusChip = ({ status }) => {
  if (!status) return <span className="text-xs text-[#9CA3AF]">—</span>;
  const cls = STATUS_STYLE[status] || "bg-slate-100 text-slate-700 border-slate-200";
  return (
    <span data-testid={`fy2026-status-chip-${status.toLowerCase()}`} className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-wider font-semibold border ${cls}`}>
      {status}
    </span>
  );
};

const formatPulledAt = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  const now = new Date();
  const diffSec = Math.max(0, Math.floor((now - d) / 1000));
  if (diffSec < 10) return "just now";
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

export default function Report2027470() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [statusTab, setStatusTab] = useState("all");
  const [downloading, setDownloading] = useState(null);
  const [zipping, setZipping] = useState(false);
  const [lastPulled, setLastPulled] = useState({}); // { [clientId]: ISOString }
  const [, setTick] = useState(0);
  const [matrixClient, setMatrixClient] = useState(null);

  useEffect(() => { load(); }, []);
  // Re-render every 15s so the "Xs ago" badges stay fresh
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 15000);
    return () => clearInterval(id);
  }, []);
  const load = async () => {
    try { const { data } = await api.get("/reports/2027-470/clients"); setClients(data); }
    catch { toast.error("Failed to load clients"); }
  };

  const downloadBidWorksheet = async (c) => {
    try {
      const { data } = await api.get(`/reports/bid-worksheet/${c.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `2027 Bid Worksheet - ${c.name || "client"}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download bid worksheet"); }
  };

  const downloadOne = async (c, fmt = "xlsx") => {
    setDownloading(c.id);
    try {
      const url_ = fmt === "docx" ? `/reports/2027-470/${c.id}/docx` : `/reports/2027-470/${c.id}`;
      const { data } = await api.get(url_, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `2027 470 - ${c.name || "client"}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      setLastPulled((prev) => ({ ...prev, [c.id]: new Date().toISOString() }));
    } catch { toast.error("Failed to generate report"); }
    finally { setDownloading(null); }
  };

  const downloadAll = async () => {
    setZipping(true);
    try {
      const { data } = await api.get(`/reports/2027-470-all`, { responseType: "blob", timeout: 180000 });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "2027_470_All_Clients.zip";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${clients.length} reports as ZIP`);
    } catch { toast.error("Failed to generate ZIP"); }
    finally { setZipping(false); }
  };

  // Map raw USAC status → tab bucket
  const bucketOf = (s) => {
    if (s === "Pending") return "pending";
    if (["Committed", "Certified", "Funded"].includes(s)) return "committed";
    if (["Denied", "Cancelled", "Rejected"].includes(s)) return "denied";
    return "none";
  };
  const counts = clients.reduce((acc, c) => {
    const b = bucketOf(c.fy2026_status);
    acc.all++; acc[b]++;
    return acc;
  }, { all: 0, pending: 0, committed: 0, denied: 0, none: 0 });

  const filtered = clients.filter((c) => {
    if (statusTab !== "all" && bucketOf(c.fy2026_status) !== statusTab) return false;
    if (!q) return true;
    return (c.name || "").toLowerCase().includes(q.toLowerCase()) || String(c.ben || "").includes(q);
  });

  return (
    <div className="p-6" data-testid="report-2027-470-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-1">Reports</p>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#002FA7]" style={{ fontFamily: "'Cabinet Grotesk', Georgia, serif" }}>
            2027 470 Preparation
            <span className="block text-xl sm:text-2xl">E-Rate History 2026-2030</span>
          </h1>
          <p className="text-sm text-[#6B7280] mt-1">Downloads the 2027 470 C1/C2 Request template with each client&apos;s Entity Name, BEN, Enrollment, NSLP, and FY2026 disbursements pre-filled.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="download-bid-worksheet-btn" onClick={async () => {
            try {
              const { data } = await api.get("/reports/bid-worksheet", { responseType: "blob" });
              const url = window.URL.createObjectURL(new Blob([data]));
              const a = document.createElement("a");
              a.href = url; a.download = "2027 Funding Year Bid Worksheet.xlsx";
              document.body.appendChild(a); a.click(); a.remove();
              window.URL.revokeObjectURL(url);
            } catch { toast.error("Failed to download bid worksheet"); }
          }} className="bg-transparent text-[#002FA7] border border-[#002FA7] hover:bg-[#002FA7]/5 rounded-none font-medium px-4 py-2 text-sm flex items-center gap-2 transition-colors">
            <FileArrowDown size={16} weight="bold" /> Bid Worksheet
          </button>
          <button data-testid="download-all-2027-btn" onClick={downloadAll} disabled={zipping} className="bg-[#002FA7] text-white hover:bg-[#002480] disabled:opacity-50 rounded-none font-medium px-4 py-2 text-sm flex items-center gap-2 transition-colors">
            <Package size={16} weight="bold" /> {zipping ? "Preparing ZIP..." : `Download All (${clients.length})`}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 border-b border-gray-200">
        {[
          { key: "all", label: "All", n: counts.all },
          { key: "pending", label: "Pending", n: counts.pending },
          { key: "committed", label: "Committed", n: counts.committed },
          { key: "denied", label: "Denied", n: counts.denied },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`status-tab-${t.key}`}
            onClick={() => setStatusTab(t.key)}
            className={`px-4 py-2 text-sm font-medium transition-colors -mb-px border-b-2 ${statusTab === t.key ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-[#6B7280] hover:text-[#1A1D20]"}`}
          >
            {t.label} <span className="ml-1.5 text-xs text-[#9CA3AF]">({t.n})</span>
          </button>
        ))}
      </div>

      <div className="mb-4 relative max-w-sm">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
        <Input data-testid="search-2027-clients" placeholder="Search by entity or BEN..." value={q} onChange={(e) => setQ(e.target.value)} className="rounded-none pl-9" />
      </div>

      <div className="border border-gray-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="data-table-header">
              <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Entity Name</TableHead>
              <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">BEN</TableHead>
              <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Enrollment</TableHead>
              <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">NSLP</TableHead>
              <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Discount</TableHead>
              <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">FY26 Status</TableHead>
              <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold w-[380px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-sm text-[#6B7280]">No clients found</TableCell></TableRow>
            ) : filtered.map((c) => (
              <TableRow key={c.id} className="border-b border-gray-100 hover:bg-gray-50">
                <TableCell className="py-3 px-4 text-[#1A1D20] font-medium">{c.name || "—"}</TableCell>
                <TableCell className="py-3 px-4 font-mono text-xs">{c.ben || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-sm">{fmt(c.enrollment)}</TableCell>
                <TableCell className="py-3 px-4 text-sm">{fmt(c.nslp)}</TableCell>
                <TableCell className="py-3 px-4 text-sm">{c.discount_rate ? `${c.discount_rate}%` : "—"}</TableCell>
                <TableCell className="py-3 px-4"><StatusChip status={c.fy2026_status} /></TableCell>
                <TableCell className="py-3 px-4">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1.5">
                      <button data-testid={`download-2027-word-${c.id}`} onClick={() => downloadOne(c, "docx")} disabled={downloading === c.id} className="text-xs bg-[#002FA7] text-white hover:bg-[#002480] disabled:opacity-50 rounded-none font-medium px-3 py-1.5 flex items-center gap-1.5">
                        <FileArrowDown size={12} weight="bold" /> Word
                      </button>
                      <button data-testid={`download-bid-${c.id}`} onClick={() => downloadBidWorksheet(c)} className="text-xs bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-3 py-1.5 flex items-center gap-1.5">
                        <FileArrowDown size={12} weight="bold" /> Bid
                      </button>
                      <button data-testid={`download-bid-matrix-${c.id}`} onClick={() => setMatrixClient(c)} className="text-xs bg-transparent text-[#002FA7] border border-[#002FA7] hover:bg-[#002FA7]/5 rounded-none font-medium px-3 py-1.5 flex items-center gap-1.5">
                        <TableIcon size={12} weight="bold" /> Bid Matrix
                      </button>
                    </div>
                    {lastPulled[c.id] && (
                      <span data-testid={`live-usac-badge-${c.id}`} className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#166534]">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
                        </span>
                        Live from USAC • {formatPulledAt(lastPulled[c.id])}
                      </span>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <BidMatrixDialog client={matrixClient} onClose={() => setMatrixClient(null)} />
    </div>
  );
}

